import { apiClient } from '../../utils/apiClient';
import { wizardService } from '../wizardService';

import type {
  // Users types
  AdminUser,
  CreateUserDto,
  UpdateUserDto,
  UserFilters,

  // Auth types
  LoginRequest,
  LoginResponse,
  User,

  // Entity types
  Representative,
  PointOfSale,
  Seller,
  Chain,
  City,
  PointStats,

  // Request types
  CreateRepresentativeRequest,
  UpdateRepresentativeRequest,
  CreatePointRequest,
  UpdatePointRequest,
  CreateSellerRequest,
  UpdateSellerRequest,

  // Response types
  PaginatedResponse,
  RepresentativeFilters,
  PointFilters,
  SellerFilters,
  SellerStats,
  RepresentativeOption,
  StoreOption,
  UserOption,

  // Dashboard types
  DashboardStats,
  GeneralManagerStats,
  ZoneManagerStats,
  RepresentativeStats,
} from '../../types/api';

// ===== Chain DTOs (compatibles con tipos existentes) =====
export interface CreateChainDto {
  name: string;
  zoneId: string;
}

export interface UpdateChainDto {
  name?: string;
  zoneId?: string;
}

// ===== City DTOs (compatibles con tipos existentes) =====
export interface CreateCityDto {
  name: string;
  zoneId: string;
}

export interface UpdateCityDto {
  name?: string;
  zoneId?: string;
}

// ===== Users Service =====
export const usersService = {
  async getAll(filters?: UserFilters): Promise<PaginatedResponse<AdminUser>> {
    const queryParams = new URLSearchParams();

    if (filters?.page) queryParams.set('page', filters.page.toString());
    if (filters?.limit) queryParams.set('limit', filters.limit.toString());
    if (filters?.search) queryParams.set('search', filters.search);
    if (filters?.role) queryParams.set('role', filters.role);
    if (filters?.status) queryParams.set('status', filters.status);
    if (filters?.sortBy) queryParams.set('sortBy', filters.sortBy);
    if (filters?.sortOrder) queryParams.set('sortOrder', filters.sortOrder);

    const queryString = queryParams.toString();
    const url = `/admin/users${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<PaginatedResponse<AdminUser>>(url);
  },

  async getById(id: string): Promise<AdminUser> {
    return apiClient.get<AdminUser>(`/admin/users/${id}`);
  },

  async create(data: CreateUserDto): Promise<AdminUser> {
    return apiClient.post<AdminUser>('/admin/users', data);
  },

  async update(id: string, data: UpdateUserDto): Promise<AdminUser> {
    return apiClient.put<AdminUser>(`/admin/users/${id}`, data);
  },

  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`/admin/users/${id}`);
  },

  async activate(id: string): Promise<AdminUser> {
    return apiClient.put<AdminUser>(`/admin/users/${id}/activate`);
  },

  async deactivate(id: string): Promise<AdminUser> {
    return apiClient.put<AdminUser>(`/admin/users/${id}/deactivate`);
  },
};

// ===== Auth Service =====
export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    return apiClient.post<LoginResponse>('/auth/login', credentials);
  },

  async getProfile(): Promise<User> {
    return apiClient.get<User>('/auth/profile');
  },
};

// ===== Chains Service =====
export const chainsService = {
  async getAll(): Promise<Chain[]> {
    return apiClient.get<Chain[]>('/admin/chains/dropdown');
  },

  async getById(id: string): Promise<Chain> {
    return apiClient.get<Chain>(`/admin/chains/${id}`);
  },

  async create(data: CreateChainDto): Promise<Chain> {
    return apiClient.post<Chain>('/admin/chains', data);
  },

  async update(id: string, data: UpdateChainDto): Promise<Chain> {
    return apiClient.patch<Chain>(`/admin/chains/${id}`, data);
  },

  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`/admin/chains/${id}`);
  },

  async getStats(): Promise<ChainStats> {
    return apiClient.get<ChainStats>('/admin/chains/stats');
  },
};

// ===== Cities Service =====
export const citiesService = {
  async getAll(): Promise<City[]> {
    return apiClient.get<City[]>('/admin/cities/dropdown');
  },

  async getById(id: string): Promise<City> {
    return apiClient.get<City>(`/admin/cities/${id}`);
  },

  async create(data: CreateCityDto): Promise<City> {
    return apiClient.post<City>('/admin/cities', data);
  },

  async update(id: string, data: UpdateCityDto): Promise<City> {
    return apiClient.patch<City>(`/admin/cities/${id}`, data);
  },

  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(`/admin/cities/${id}`);
  },

  async getStats(): Promise<CityStats> {
    return apiClient.get<CityStats>('/admin/cities/stats');
  },
};

// ===== Representatives Service =====
export const representativesService = {
  async getAll(
    filters?: RepresentativeFilters
  ): Promise<PaginatedResponse<Representative>> {
    const queryParams = new URLSearchParams();

    if (filters?.page) queryParams.set('page', filters.page.toString());
    if (filters?.limit) queryParams.set('limit', filters.limit.toString());
    if (filters?.search) queryParams.set('search', filters.search);
    if (filters?.zoneId) queryParams.set('zoneId', filters.zoneId);
    if (filters?.sortBy) queryParams.set('sortBy', filters.sortBy);
    if (filters?.sortOrder) queryParams.set('sortOrder', filters.sortOrder);

    const queryString = queryParams.toString();
    const url = `/admin/representatives${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<PaginatedResponse<Representative>>(url);
  },

  async getById(id: string): Promise<Representative> {
    return apiClient.get<Representative>(`/admin/representatives/${id}`);
  },

  async create(data: CreateRepresentativeRequest): Promise<Representative> {
    return apiClient.post<Representative>('/admin/representatives', data);
  },

  async update(
    id: string,
    data: UpdateRepresentativeRequest
  ): Promise<Representative> {
    return apiClient.patch<Representative>(
      `/admin/representatives/${id}`,
      data
    );
  },

  async delete(id: string): Promise<undefined> {
    return apiClient.delete<undefined>(`/admin/representatives/${id}`);
  },

  // Auxiliary endpoints
  async getAvailableUsers(): Promise<UserOption[]> {
    return apiClient.get<UserOption[]>('/admin/representatives/users');
  },

  async getZones(): Promise<Array<{ id: string; name: string }>> {
    return apiClient.get<Array<{ id: string; name: string }>>(
      '/admin/representatives/zones'
    );
  },
};

// ===== Points Service =====
export const pointsService = {
  async getAll(
    filters?: PointFilters
  ): Promise<PaginatedResponse<PointOfSale>> {
    const queryParams = new URLSearchParams();

    if (filters?.page) queryParams.set('page', filters.page.toString());
    if (filters?.limit) queryParams.set('limit', filters.limit.toString());
    if (filters?.search) queryParams.set('search', filters.search);
    if (filters?.cityId) queryParams.set('cityId', filters.cityId);
    if (filters?.chainId) queryParams.set('chainId', filters.chainId);
    if (filters?.representativeId)
      queryParams.set('representativeId', filters.representativeId);

    const queryString = queryParams.toString();
    const url = `/admin/points${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<PaginatedResponse<PointOfSale>>(url);
  },

  async getById(id: string): Promise<PointOfSale> {
    return apiClient.get<PointOfSale>(`/admin/points/${id}`);
  },

  async create(data: CreatePointRequest): Promise<PointOfSale> {
    return apiClient.post<PointOfSale>('/admin/points', data);
  },

  async update(id: string, data: UpdatePointRequest): Promise<PointOfSale> {
    return apiClient.patch<PointOfSale>(`/admin/points/${id}`, data);
  },

  async delete(id: string): Promise<undefined> {
    return apiClient.delete<undefined>(`/admin/points/${id}`);
  },

  async getStats(): Promise<PointStats> {
    return apiClient.get<PointStats>('/admin/points/stats');
  },

  // Auxiliary endpoints
  async getCities(): Promise<
    Array<{ id: string; name: string; zone: { name: string } }>
  > {
    return apiClient.get<
      Array<{ id: string; name: string; zone: { name: string } }>
    >('/admin/points/cities');
  },

  async getChains(): Promise<
    Array<{ id: string; name: string; zone: { name: string } }>
  > {
    return apiClient.get<
      Array<{ id: string; name: string; zone: { name: string } }>
    >('/admin/points/chains');
  },

  async getRepresentatives(): Promise<RepresentativeOption[]> {
    return apiClient.get<RepresentativeOption[]>(
      '/admin/points/representatives'
    );
  },
};

// ===== Sellers Service =====
export const sellersService = {
  async getAll(filters?: SellerFilters): Promise<PaginatedResponse<Seller>> {
    const queryParams = new URLSearchParams();

    if (filters?.page) queryParams.set('page', filters.page.toString());
    if (filters?.limit) queryParams.set('limit', filters.limit.toString());
    if (filters?.search) queryParams.set('search', filters.search);
    if (filters?.storeId) queryParams.set('storeId', filters.storeId);
    if (filters?.representativeId)
      queryParams.set('representativeId', filters.representativeId);
    if (filters?.cityId) queryParams.set('cityId', filters.cityId);
    if (filters?.zoneId) queryParams.set('zoneId', filters.zoneId);
    if (filters?.representativeZoneId)
      queryParams.set('representativeZoneId', filters.representativeZoneId);
    if (filters?.active !== undefined)
      queryParams.set('active', filters.active.toString());
    if (filters?.sortBy) queryParams.set('sortBy', filters.sortBy);
    if (filters?.sortOrder) queryParams.set('sortOrder', filters.sortOrder);

    const queryString = queryParams.toString();
    const url = `/admin/sellers${queryString ? `?${queryString}` : ''}`;

    return apiClient.get<PaginatedResponse<Seller>>(url);
  },

  async getById(id: string): Promise<Seller> {
    return apiClient.get<Seller>(`/admin/sellers/${id}`);
  },

  async create(data: CreateSellerRequest): Promise<Seller> {
    return apiClient.post<Seller>('/admin/sellers', data);
  },

  async update(id: string, data: UpdateSellerRequest): Promise<Seller> {
    return apiClient.put<Seller>(`/admin/sellers/${id}`, data);
  },

  async delete(id: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/admin/sellers/${id}`);
  },

  async activate(id: string): Promise<Seller> {
    return apiClient.put<Seller>(`/admin/sellers/${id}/activate`);
  },

  async deactivate(id: string): Promise<Seller> {
    return apiClient.put<Seller>(`/admin/sellers/${id}/deactivate`);
  },

  // Auxiliary endpoints
  async getStats(): Promise<SellerStats> {
    return apiClient.get<SellerStats>('/admin/sellers/stats');
  },

  async getRepresentatives(): Promise<RepresentativeOption[]> {
    return apiClient.get<RepresentativeOption[]>(
      '/admin/sellers/representatives'
    );
  },

  async getStores(zoneId?: string): Promise<StoreOption[]> {
    const url = `/admin/sellers/stores${zoneId ? `?zoneId=${zoneId}` : ''}`;
    return apiClient.get<StoreOption[]>(url);
  },

  async getAvailableUsers(): Promise<UserOption[]> {
    return apiClient.get<UserOption[]>('/admin/sellers/users');
  },
};

// ===== Admin Service =====
export const adminService = {
  async getDashboardStats(): Promise<DashboardStats> {
    return apiClient.get<DashboardStats>('/admin/dashboard/stats');
  },

  async getGeneralManagerStats(): Promise<GeneralManagerStats> {
    return apiClient.get<GeneralManagerStats>('/admin/reports/stats/general');
  },

  async getZoneManagerStats(zoneId: string): Promise<ZoneManagerStats> {
    return apiClient.get<ZoneManagerStats>(
      `/admin/reports/stats/zone/${zoneId}`
    );
  },

  async getRepresentativeStats(
    representativeId: string
  ): Promise<RepresentativeStats> {
    return apiClient.get<RepresentativeStats>(
      `/admin/reports/stats/representative/${representativeId}`
    );
  },
};

// ===== Export all services =====
export const apiServices = {
  admin: adminService,
  auth: authService,
  users: usersService,
  representatives: representativesService,
  points: pointsService,
  sellers: sellersService,
  chains: chainsService,
  cities: citiesService,
  wizard: wizardService,
};

export default apiServices;
